import { Controller } from "@hotwired/stimulus"
import { StreamActions } from "@hotwired/turbo"
import { setHidden } from "../helpers/hidden"

// The action name is the server's (BoothAvailabilityBroadcast::ACTION); the
// event name is ours. They are not the same string on purpose: renaming the
// wire format is a coordinated change, renaming a DOM event is not.
const ACTION = "booth_availability"
const EVENT = "booth-availability"

const SVG = "http://www.w3.org/2000/svg"

// Lucide's check and x, for the chosen badge and the tray's Remove.
const CHECK = ["M20 6 9 17l-5-5"]
const CROSS = ["M18 6 6 18", "m6 6 12 12"]

function svgElement(name, attributes = {}) {
  const element = document.createElementNS(SVG, name)
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value))
  return element
}

function icon(paths, attributes) {
  const svg = svgElement("svg", { viewBox: "0 0 24 24", "aria-hidden": "true", ...attributes })
  paths.forEach((d) => svg.append(svgElement("path", { d })))
  return svg
}

// The custom Turbo Stream action the server broadcasts (docs/03 Realtime).
// It carries {booth_id, status} and no HTML, so nothing is replaced and
// nobody's keyboard focus is thrown away.
//
// It dispatches rather than reaching for the controller: a stream action is a
// global, and a global that knows how to find a Stimulus instance is a global
// that breaks the day the identifier changes.
StreamActions[ACTION] = function () {
  document.dispatchEvent(new CustomEvent(EVENT, {
    detail: { [this.getAttribute("booth-id")]: this.getAttribute("status") }
  }))
}

// The booth picker (docs/07 "Exhibitor registration", D30).
//
// The map and the list are two renderings of the same booths and are always
// in step: choosing on one shows on the other, because a person who switched
// views mid-decision has not changed their mind.
//
// Three rules that are not obvious from the markup:
//
//   1. The list is the DEFAULT, not a fallback, whenever a booth would be
//      drawn smaller than a 44 px tap (docs/07 "Size rule"). That can only be
//      measured in the browser, so it is measured here on connect and again
//      on resize.
//   2. In a single-booth zone a new choice REPLACES the old one. Silently
//      ignoring the second click is how somebody ends up with the booth they
//      did not want.
//   3. Over the limit, the picker says so. An inline message, not a dropped
//      click: a control that ignores you reads as broken.
//
// The server is the only authority on availability (docs/08). Everything here
// is a proposal.
export default class extends Controller {
  static targets = [
    "svg", "booth", "map", "list", "row", "rowStatus", "checkbox",
    "mapToggle", "listToggle", "callout", "message", "live", "summary", "inputs",
    "cable", "status", "listCaption", "swatch", "overlay"
  ]

  static values = {
    limit: { type: Number, default: 1 },
    feeMinor: { type: Number, default: 0 },
    readonly: { type: Boolean, default: false },
    selected: { type: Array, default: [] },
    availabilityUrl: String,
    zone: String,
    zones: { type: Object, default: {} }
  }

  // The smallest tap a finger can reliably hit (docs/07, WCAG 2.5.8).
  static MIN_TAP = 44

  // How long a dropped connection is allowed to look normal. Under this and a
  // reconnect is invisible; over it, the picker has to admit that what it is
  // showing may be out of date (docs/07).
  static GRACE_MS = 5000

  // How long "Updated just now" stays up after a catch-up (docs/07).
  static UPDATED_MS = 3000

  connect() {
    this.selected = new Set(this.selectedValue.map(String))
    this.focusedIndex = 0
    // A ResizeObserver rather than a window resize listener: the picker starts
    // life inside a HIDDEN wizard step, where every box measures zero. The
    // measurement that decides map-or-list can only be taken once it is on
    // screen, and becoming visible is a size change — which is what this sees
    // and a window listener never would.
    this.sizeObserver = new ResizeObserver(() => this.measure())
    this.sizeObserver.observe(this.svgTarget)

    this.onBroadcast = (event) => this.reconcile(event.detail)
    document.addEventListener(EVENT, this.onBroadcast)

    this.watchConnection()

    this.render()
    this.measure()
  }

  disconnect() {
    this.sizeObserver?.disconnect()
    document.removeEventListener(EVENT, this.onBroadcast)
    this.connectionObserver?.disconnect()
    clearTimeout(this.graceTimer)
    clearTimeout(this.updatedTimer)
  }

  // --- The connection -------------------------------------------------------

  // `<turbo-cable-stream-source>` sets and removes a `connected` attribute of
  // its own, so watching it needs no hook into Turbo's cable and survives a
  // turbo-rails upgrade that changes how the subscription is made.
  watchConnection() {
    if (!this.hasCableTarget) return

    this.connectionObserver = new MutationObserver(() => this.connectionChanged())
    this.connectionObserver.observe(this.cableTarget, { attributes: true, attributeFilter: ["connected"] })
  }

  connectionChanged() {
    if (this.cableTarget.hasAttribute("connected")) {
      clearTimeout(this.graceTimer)
      // Only catch up if something was actually missed. A first connect has
      // nothing to catch up on, and re-fetching on it would make every page
      // load ask twice.
      if (this.wasDropped) this.catchUp()
      this.wasDropped = false
      if (!this.updatedTimer) this.setStatus("live", "● Live")
      return
    }

    this.wasDropped = true
    clearTimeout(this.graceTimer)
    this.graceTimer = setTimeout(() => {
      this.setStatus("reconnecting", "○ Reconnecting… availability may be out of date")
    }, this.constructor.GRACE_MS)
  }

  // Everything that happened while the cable was down, in one request. Asking
  // for the whole picture is the only honest way back: a picker that was
  // offline cannot know which updates it missed.
  async catchUp() {
    if (!this.availabilityUrlValue) return

    try {
      const response = await fetch(this.availabilityUrlValue, { headers: { Accept: "application/json" } })
      if (!response.ok) return

      this.reconcile(await response.json())
      this.setStatus("updated", "● Updated just now")
      clearTimeout(this.updatedTimer)
      this.updatedTimer = setTimeout(() => {
        this.updatedTimer = null
        this.setStatus("live", "● Live")
      }, this.constructor.UPDATED_MS)
    } catch {
      // A failed catch-up leaves the reconnecting message standing, which is
      // the truthful state: we still do not know what changed.
    }
  }

  setStatus(state, text) {
    if (!this.hasStatusTarget) return

    this.statusTarget.dataset.state = state
    this.statusTarget.textContent = text
  }

  // --- The chosen zone ------------------------------------------------------

  // Switching zones must NOT reload the page: the wizard is one form, and a
  // reload is everything typed in step 3 gone (docs/07). Every accepting
  // zone's window, limit and fee came with the page, so this re-points the
  // same booths at a different one.
  // The wizard's step 1 says a zone was chosen (P8.22). It arrives on
  // `document` because the picker sits inside the wizard and a bubbling event
  // from an ancestor would never reach it.
  zoneChosen(event) {
    this.setZone(event.detail.slug)
  }

  setZone(slug) {
    if (slug === this.zoneValue) return

    this.zoneValue = slug
    // The selection belongs to the old zone. Keeping it would submit booths
    // from a zone the person is no longer registering for.
    this.selected.clear()
    this.clearMessage()
    this.applyZone()
  }

  // Stimulus fires value-changed callbacks during INITIALIZE, before connect,
  // so `this.selected` does not exist yet and the first render throws. The
  // server already rendered the right zone, so there is nothing to apply then
  // anyway — this is only for a zone changed afterwards.
  zoneValueChanged() {
    if (this.selected && this.hasSvgTarget) this.applyZone()
  }

  applyZone() {
    const settings = this.zonesValue[this.zoneValue]
    if (!settings) return

    this.limitValue = settings.limit
    this.feeMinorValue = settings.feeMinor
    // The viewport's Fit returns to the zone, so it has to learn the new one —
    // and frame it, so the readout and the window match the zone now shown.
    this.svgTarget.dataset.homeViewBox = settings.viewBox
    this.svgTarget.setAttribute("viewBox", settings.viewBox)
    this.viewport?.fit()
    this.svgTarget.setAttribute("aria-label",
      `Booth map for ${settings.name}. Arrow keys move between booths, Space chooses one.`)
    // The key's stands are drawn in the zone's colour, like the map's.
    if (settings.palette) this.swatchTargets.forEach((swatch) => { swatch.dataset.zone = settings.palette })

    this.boothTargets.forEach((booth) => {
      const inZone = booth.dataset.zoneKey === this.zoneValue
      booth.dataset.inZone = String(inZone)
      booth.dataset.selectable = String(inZone && booth.dataset.availability === "available")
      booth.setAttribute("role", booth.dataset.selectable === "true" ? "checkbox" : "img")
    })

    this.rowTargets.forEach((row) => {
      setHidden(row, row.dataset.zoneKey !== this.zoneValue)
    })

    if (this.hasListCaptionTarget) {
      this.listCaptionTarget.textContent =
        `Every booth in ${settings.name}, with its size and whether it is free`
    }
    if (this.hasSummaryTarget) {
      this.summaryTarget.textContent =
        settings.limit === 1 ? "Choose one booth." : `Choose up to ${settings.limit} booths.`
    }

    this.focusedIndex = 0
    this.render()
    this.measure()
  }

  get viewport() {
    return this.application.getControllerForElementAndIdentifier(this.mapTarget, "map-viewport")
  }

  // --- Choosing -------------------------------------------------------------

  get selectableBooths() {
    return this.boothTargets.filter((booth) => booth.dataset.selectable === "true")
  }

  // Delegated from the <svg>, so a booth that became selectable after the
  // page was rendered — the zone changed in the browser — is as clickable as
  // one that was. The viewport's capture-phase click handler has already
  // stopped the click when it was the end of a pan.
  //
  // A taken stand in the zone says WHY it did nothing (the user, 2026-10-18:
  // "there is no prompt saying its already booked"). The <title> says it on
  // hover, but a phone has no hover, and a tap that does nothing reads as a
  // broken map. Other zones' stands take no pointer events at all.
  toggle(event) {
    const booth = event.target.closest?.("[data-booth-id]")
    if (!booth || !this.element.contains(booth)) return

    if (booth.dataset.selectable === "true") this.choose(booth.dataset.boothId)
    else if (booth.dataset.inZone === "true") this.explain(booth)
  }

  explain(booth) {
    const code = booth.dataset.code
    const reasons = {
      booked: `Booth ${code} is already booked. Choose one of the outlined stands.`,
      held: `Booth ${code} is on hold for another company that has not confirmed yet. If they don't, it comes free again.`
    }
    this.showMessage(reasons[booth.dataset.availability] || `Booth ${code} is not available.`)
  }

  toggleFromList(event) {
    this.choose(event.currentTarget.dataset.boothId)
  }

  choose(id) {
    if (this.readonlyValue) return

    if (this.selected.has(id)) {
      this.selected.delete(id)
      this.clearMessage()
    } else if (this.limitValue === 1) {
      // A new choice replaces the old one rather than being refused: in a
      // one-booth zone "choose another" is the only thing a second click can
      // sensibly mean.
      this.selected.clear()
      this.selected.add(id)
      this.clearMessage()
    } else if (this.selected.size >= this.limitValue) {
      // An inline message, which is also announced: the <p> carries
      // role="alert" (docs/07 "choosing more than the limit shows an inline
      // message instead of silently ignoring the click").
      this.showMessage(`You can choose ${this.limitValue} booths in this zone. Remove one first.`)
      this.render()
      return
    } else {
      this.selected.add(id)
      this.clearMessage()
    }

    this.render()
  }

  remove(event) {
    this.selected.delete(event.currentTarget.dataset.boothId)
    this.clearMessage()
    this.render()
  }

  // --- Keyboard -------------------------------------------------------------

  // Arrow keys move between booths in READING ORDER — the order the server
  // sorted them into, top to bottom then left to right — because that is the
  // order the map is read in and the order the list is in.
  navigate(event) {
    const booths = this.selectableBooths
    if (booths.length === 0) return

    const forward = ["ArrowRight", "ArrowDown"].includes(event.key)
    const back = ["ArrowLeft", "ArrowUp"].includes(event.key)

    // A tap focuses the stand it chose, so the keys carry on from there,
    // and Space acts on the stand the ring is drawn round.
    const current = booths.indexOf(document.activeElement)
    if (current >= 0) this.focusedIndex = current

    if (forward || back) {
      event.preventDefault()
      const step = forward ? 1 : -1
      this.focusedIndex = (this.focusedIndex + step + booths.length) % booths.length
      this.focus(booths[this.focusedIndex])
      return
    }

    if (event.key === " " || event.key === "Enter") {
      event.preventDefault()
      const booth = booths[this.focusedIndex]
      if (booth) this.choose(booth.dataset.boothId)
    }
  }

  focus(booth) {
    this.selectableBooths.forEach((each) => each.setAttribute("tabindex", "-1"))
    booth.setAttribute("tabindex", "0")
    booth.focus({ preventScroll: true })
    this.say(booth.getAttribute("aria-label"))
  }

  // --- Live updates (the reconcile P8.21 drives) ----------------------------

  // `states` is { boothId: availability }. Called by the availability stream
  // and by the reconnect refresh. It must NOT move focus: somebody mid-choice
  // whose cursor jumps because a stranger took a booth two aisles away has
  // lost their place for no reason (T17b).
  reconcile(states) {
    const lost = []
    let added = false

    Object.entries(states).forEach(([id, availability]) => {
      const booth = this.boothTargets.find((each) => each.dataset.boothId === String(id))
      // A stand added since this page was drawn: nothing here to update, and
      // no HTML comes down the wire to draw it with. Said once, below. (A
      // page drawn in the moment between the insert and its broadcast has
      // the stand already; "added" is no state for it to take.)
      if (availability === "added") { added ||= !booth; return }
      if (!booth) return

      const wasSelectable = booth.dataset.selectable === "true"
      booth.dataset.availability = availability
      booth.dataset.selectable = String(availability === "available" && booth.dataset.inZone === "true")

      if (availability === "removed") {
        // Deleted by staff: off the drawing and out of the list, and let go
        // of if it was chosen — the submit would refuse it.
        // setAttribute, not `.hidden`: an SVG <g> has no hidden property.
        booth.setAttribute("hidden", "")
        this.rowTargets.find((row) => row.dataset.boothId === String(id))?.setAttribute("hidden", "")
        if (this.selected.delete(String(id))) lost.push(booth.dataset.code)
        this.say(`Booth ${booth.dataset.code} was removed from the plan`)
      } else if (booth.dataset.selectable !== "true" && this.selected.has(String(id))) {
        this.selected.delete(String(id))
        lost.push(booth.dataset.code)
        this.say(`Booth ${booth.dataset.code} is no longer available`)
      } else if (!wasSelectable && booth.dataset.selectable === "true") {
        this.say(`Booth ${booth.dataset.code} is free again`)
      }
    })

    if (added) this.say("New stands were added to the plan. Reload the page to see them.")

    this.render()

    // Letting go of the booth is the integrity half: it cannot be submitted.
    // Saying WHICH booth went is the other half, and from step 3 the summary
    // is the only place a person can see it (docs/07 "Persistent summary").
    if (lost.length > 0) this.dispatch("lost", { detail: { codes: lost } })
  }

  // --- The two views --------------------------------------------------------

  // A person's own choice, which the size rule must not undo: "the map is one
  // tap away" means it STAYS after that tap. Auto-switching on every
  // measurement threw somebody who tapped Map straight back to the list.
  showMap() { this.chosenView = "map"; this.setView("map") }
  showList() { this.chosenView = "list"; this.setView("list") }

  setView(view) {
    // Nothing to do, and doing it anyway means resizing the map, which wakes
    // the observer that called this.
    if (this.view === view) return

    this.view = view
    this.mapTarget.hidden = view !== "map"
    this.listTarget.hidden = view !== "list"
    this.mapToggleTarget.setAttribute("aria-pressed", String(view === "map"))
    this.listToggleTarget.setAttribute("aria-pressed", String(view === "list"))
    this.mapToggleTarget.classList.toggle("ghost", view !== "map")
    this.listToggleTarget.classList.toggle("ghost", view === "map")
  }

  // The size rule. If the smallest selectable booth is drawn below a 44 px
  // tap, the list becomes the default and the map stays one tap away — which
  // is what docs/07 asks for instead of pretending a 20 px rectangle is
  // tappable.
  measure() {
    const booths = this.selectableBooths
    if (booths.length === 0) return

    const smallest = booths.reduce((min, booth) => {
      const box = booth.getBoundingClientRect()
      return Math.min(min, box.width, box.height)
    }, Infinity)

    // Zero means the map is not laid out yet (hidden, or not painted). A
    // measurement of nothing is not a measurement, and acting on it would put
    // every visitor into the list view.
    if (smallest === 0 || smallest === Infinity) return
    // The size rule decides the DEFAULT, not what somebody is looking at now.
    if (this.chosenView) return
    // Nor what they have zoomed to. Measured only at the zone's own window:
    // zooming out to the whole venue makes every booth small on purpose.
    const home = this.svgTarget.dataset.homeViewBox
    if (home && this.svgTarget.getAttribute("viewBox") !== home) return

    this.setView(smallest >= this.constructor.MIN_TAP ? "map" : "list")
  }

  // --- Rendering ------------------------------------------------------------

  render() {
    const ids = [...this.selected]

    this.boothTargets.forEach((booth) => {
      const chosen = this.selected.has(booth.dataset.boothId)
      booth.classList.toggle("is-selected", chosen)
      if (booth.getAttribute("role") === "checkbox") booth.setAttribute("aria-checked", String(chosen))
    })

    this.renderOverlay()

    this.checkboxTargets.forEach((box) => {
      box.checked = this.selected.has(box.dataset.boothId)
    })

    this.rowTargets.forEach((row) => {
      row.classList.toggle("is-selected", this.selected.has(row.dataset.boothId))
    })

    this.renderInputs(ids)
    this.renderCallout(ids)

    // The wizard's summary and its Continue button read this. The picker does
    // not know what a wizard is, and should not: it says what was chosen and
    // whoever cares listens.
    this.dispatch("change", {
      detail: {
        zone: this.zoneValue,
        feeMinor: this.feeMinorValue,
        booths: ids.map((id) => {
          const booth = this.boothTargets.find((each) => each.dataset.boothId === id)
          return { id, code: booth?.dataset.code, size: booth?.dataset.size,
                   status: booth?.dataset.availability }
        })
      }
    })
  }

  // The layer drawn after every booth: a check badge on each chosen stand
  // and the ring round the one with keyboard focus. Both used to be strokes
  // on the stand itself, and the next stand in the document painted over
  // them. Called on every render and on focus moving inside the map.
  renderOverlay() {
    if (!this.hasOverlayTarget) return

    const marks = []

    // Only for keyboard focus: a ring after every tap would read as a second
    // selection state. Drawn before the badges, so a badge sits on the ring.
    const focused = this.boothTargets.find((booth) => booth === document.activeElement)
    if (focused?.matches(":focus-visible")) marks.push(...this.ringFor(focused))

    this.boothTargets.forEach((booth) => {
      if (this.selected.has(booth.dataset.boothId) && !booth.hasAttribute("hidden")) marks.push(this.badgeFor(booth))
    })

    this.overlayTarget.replaceChildren(...marks)
  }

  // Two copies of the stand's own rectangle, rotation and all: a dark one
  // under a cream one, so the ring reads on a filled stand and on the ground.
  ringFor(booth) {
    const shape = booth.querySelector(".booth-shape")
    return ["booth-picker-ring-under", "booth-picker-ring"].map((name) => {
      const ring = shape.cloneNode(false)
      ring.removeAttribute("data-zone")
      ring.removeAttribute("data-booth-picker-target")
      ring.setAttribute("class", name)
      return ring
    })
  }

  // A disc on the stand's top-right corner with a check in it. It turns with
  // the stand (D39), and the check is turned back so it always reads upright.
  badgeFor(booth) {
    const shape = booth.querySelector(".booth-shape")
    const [x, y, width, height] = ["x", "y", "width", "height"].map((name) => Number(shape.getAttribute(name)))
    const radius = Math.min(width, height) * 0.17
    const cx = x + width - radius * 0.6
    const cy = y + radius * 0.6
    const angle = Number(/rotate\(\s*(-?[\d.]+)/.exec(shape.getAttribute("transform") || "")?.[1] || 0)

    const badge = svgElement("g", { class: "booth-picker-badge", "data-zone": shape.dataset.zone || "" })
    if (shape.hasAttribute("transform")) badge.setAttribute("transform", shape.getAttribute("transform"))
    badge.append(svgElement("circle", { cx, cy, r: radius }))

    const check = svgElement("g", { transform: `rotate(${-angle} ${cx} ${cy})` })
    const size = radius * 1.2
    check.append(icon(CHECK, { x: cx - size / 2, y: cy - size / 2, width: size, height: size }))
    badge.append(check)
    return badge
  }

  // What the form submits. Rebuilt wholesale rather than patched: a stale
  // hidden input is a booth somebody did not ask for.
  renderInputs(ids) {
    this.inputsTarget.replaceChildren(
      ...ids.map((id) => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "booth_ids[]"
        input.value = id
        return input
      })
    )
  }

  // The tray: one chip per chosen booth, each with its own Remove, and a
  // dashed chip saying how many more can be chosen. It is there from the
  // first moment, empty, so "Your booths" is a place before it is a list.
  renderCallout(ids) {
    const remaining = this.readonlyValue ? 0 : Math.max(this.limitValue - ids.length, 0)
    if (ids.length === 0 && remaining === 0) {
      this.calloutTarget.hidden = true
      this.calloutTarget.replaceChildren()
      return
    }

    const head = document.createElement("p")
    head.className = "booth-picker-tray-head"
    const title = document.createElement("span")
    title.textContent = "Your booths"
    const total = document.createElement("span")
    total.className = "mono"
    total.dataset.boothPickerTotal = ""
    total.textContent = this.trayCount(ids.length)
    head.append(title, total)

    const list = document.createElement("ul")
    list.className = "booth-picker-chips"

    ids.forEach((id) => {
      const booth = this.boothTargets.find((each) => each.dataset.boothId === id)
      if (!booth) return

      const item = document.createElement("li")
      item.className = "booth-picker-chip"

      const code = document.createElement("span")
      code.className = "mono"
      code.textContent = booth.dataset.code

      const size = document.createElement("span")
      size.textContent = booth.dataset.size

      item.append(code, size)

      if (!this.readonlyValue) {
        const remove = document.createElement("button")
        remove.type = "button"
        remove.dataset.boothId = id
        remove.dataset.action = "booth-picker#remove"
        remove.setAttribute("aria-label", `Remove booth ${booth.dataset.code}`)
        remove.append(icon(CROSS, { class: "ico sm" }))
        item.append(remove)
      }

      list.append(item)
    })

    if (remaining > 0) {
      const slot = document.createElement("li")
      slot.className = "booth-picker-chip"
      slot.dataset.state = "empty"
      slot.textContent = ids.length === 0
        ? (this.limitValue === 1 ? "Choose a booth on the map" : `Choose up to ${this.limitValue} booths on the map`)
        : `You can add ${remaining} more`
      list.append(slot)
    }

    this.calloutTarget.replaceChildren(head, list)
    this.calloutTarget.hidden = false
  }

  // "1 of 3 · MVR 3,000": the count against the limit, always, and the fee
  // once there is something to pay for.
  trayCount(count) {
    const of = `${count} of ${this.limitValue}`
    if (count === 0) return of
    return this.feeMinorValue === 0 ? `${of} · no fee` : `${of} · MVR ${((this.feeMinorValue * count) / 100).toLocaleString("en-US")}`
  }

  say(message) {
    this.liveTarget.textContent = message
  }

  showMessage(text) {
    this.messageTarget.textContent = text
    this.messageTarget.hidden = false
  }

  clearMessage() {
    this.messageTarget.hidden = true
    this.messageTarget.textContent = ""
  }
}
